import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { InfoTooltipComponent } from './info-tooltip.component';

@Component({
  selector: 'mat-file-upload',
  template: `
    <div class="flex flex-col gap-2" data-cy="upload-button-component">
      @if (labelText) {
        <span class="text-sm font-medium">{{ labelText }} <app-info-tooltip [tooltip]="info"></app-info-tooltip> </span>
      }

      <div class="flex items-center gap-2">
        <button
          class="dd-action-button"
          mat-flat-button
          [type]="selectFilesButtonType"
          (click)="fileInput.click()"
          [attr.aria-label]="selectButtonText">
          {{ selectButtonText }}
          <input
            #fileInput
            type="file"
            class="hidden"
            [accept]="acceptedTypes"
            [multiple]="allowMultipleFiles"
            (change)="filesChanged($event.target['files'])" />
        </button>

        @if (showUploadButton) {
          <button
            class="dd-action-button"
            mat-flat-button
            [type]="uploadButtonType"
            [disabled]="!selectedFiles"
            (click)="uploadFiles()"
            [attr.aria-label]="uploadButtonText">
            {{ uploadButtonText }}
          </button>
        }
      </div>

      @if (selectedFiles?.length) {
        <div class="fuse-card p-4 my-4 border">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <mat-icon>description</mat-icon>
              <span class="text-sm">{{ selectedFileText }}</span>
            </div>
            <button mat-icon-button (click)="filesChanged(null)" aria-label="Remove Selected File(s)">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
      }

      @if (descriptionText) {
        <p class="text-sm text-accent">
          {{ descriptionText }}
        </p>
      }
    </div>
  `,
  styles: [
    '.file-input-button { margin-right: 8px !important }',
    '.file-input-text { font-size: 14px !important; margin-right: 8px !important }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatIconModule, MatButtonModule, InfoTooltipComponent],
})
export class MatUploadButtonComponent {
  @Input() labelText = 'Select File(s)';
  @Input() info: string;
  @Input() descriptionText = 'Select File(s)';
  @Input() selectButtonText = 'Select File(s)';
  @Input() selectFilesButtonType: 'button' | 'menu' | 'reset' | 'submit' = 'button';
  @Input() uploadButtonText = 'Upload File(s)';
  @Input() uploadButtonType: 'button' | 'menu' | 'reset' | 'submit' = 'button';
  @Input() allowMultipleFiles = false;
  @Input() showUploadButton = true;
  @Input() acceptedTypes = '*.*';
  @Input() customSvgIcon?: string = null;
  @Output() uploadClicked: EventEmitter<FileList> = new EventEmitter<FileList>();
  @Output() selectedFilesChanged: EventEmitter<FileList> = new EventEmitter<FileList>();

  @ViewChild('fileInput') fileInputRef: ElementRef;
  selectedFiles: FileList;
  selectedFileText = '';

  filesChanged(files?: FileList): void {
    this.selectedFiles = files;
    this.selectedFilesChanged.emit(this.selectedFiles);
    if (this.selectedFiles) {
      const numSelectedFiles = this.selectedFiles.length;
      this.selectedFileText =
        numSelectedFiles === 1 ? this.selectedFiles[0].name : `${numSelectedFiles} files selected`;
    } else {
      this.selectedFileText = '';
      this.resetFileInput();
    }
  }

  uploadFiles(): void {
    this.uploadClicked.emit(this.selectedFiles);
    this.resetFileInput();
  }

  resetFileInput(): void {
    this.fileInputRef.nativeElement.value = '';
  }
}
